import { useCallback, useEffect, useState, type MouseEvent } from 'react';

type UseCameraStreamFullscreenParams = {
  /** When false, double-click / F-key toggle is ignored (e.g. stream disconnected). */
  canToggle: boolean;
};

export const useCameraStreamFullscreen = ({ canToggle }: UseCameraStreamFullscreenParams) => {
  const [isFullscreen, setIsFullscreen] = useState(isDocumentFullscreen);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(isDocumentFullscreen());
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(
    (element: HTMLElement) => {
      if (!canToggle) return;
      void toggleElementFullscreen(element);
    },
    [canToggle]
  );

  const handleDoubleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      toggleFullscreen(event.currentTarget);
    },
    [toggleFullscreen]
  );

  return {
    toggleFullscreen,
    handleDoubleClick,
    /** False in Fullscreen API mode so the menu stays inside the fullscreen element. */
    usePortalForWidgetMenu: !isFullscreen,
  };
};

/** Run `action` after leaving Fullscreen API mode (no-op when not fullscreen). */
export const runAfterExitingFullscreen = (action: () => void): void => {
  if (!isDocumentFullscreen()) {
    action();
    return;
  }
  void exitDocumentFullscreen().finally(action);
};

const isDocumentFullscreen = (): boolean => Boolean(document.fullscreenElement);

const exitDocumentFullscreen = (): Promise<void> => {
  if (!document.fullscreenElement) return Promise.resolve();
  return document.exitFullscreen().catch(() => undefined);
};

const toggleElementFullscreen = (element: HTMLElement): Promise<void> =>
  isDocumentFullscreen() ? exitDocumentFullscreen() : element.requestFullscreen();
